import React, { useEffect } from "react";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../../component/common/Routes/AppRoutes";
import {
  fetchRoleDetailsRequest,
  resetRoleState,
  updateRoleRequest
} from "../../redux/Roles/actions/RolesActions";
import TextInputField from "../../component/ui/FormFields/TextInputField";
import PermissionsTable from "../../component/ui/PermissionsTable";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import Loading from "../../component/ui/Loading";
import CustomButton from "../../component/ui/FormFields/Button";

export default function EditRoleForm() {
  const location = useLocation();
  const dispatch = useDispatch();
  const roleData = location.state?.roleData || {};
  const roleDetail = useSelector((state) => state.roles || {});
  const viewLoading = roleDetail?.loading;
  const editLoading = roleDetail?.updateLoading;
  const id = roleData.id;
  const roleId = id;
  useEffect(() => {
    if (roleId) {
      dispatch(fetchRoleDetailsRequest(roleId));
    }
  }, [dispatch, roleId]);

  const status = roleData.status === 0 ? "Active" : "Inactive";
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...roleData,
      name: roleData.name,
      status: roleData.status === 0 ? "Active" : "Inactive",
      permissions: roleData.permissions || {}
    }
  });
  useEffect(() => {
    if (roleData) {
      setValue("roleName", roleData.name);
      setValue("status", roleData.status === 0 ? "Active" : "Inactive");
      setValue("permissions", roleData.permissions || {});
    }
  }, [roleData, setValue]);
  const role = useSelector((state) => state.roles);
  const { loading, success, error } = useSelector((state) => state.roles);
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(resetRoleState());
  }, []);

  const handlePermissionChange = (category, perm, checked) => {
    const permissionsList = [
      "add",
      "view",
      "edit",
      "delete",
      "allaccess",
      "noaccess"
    ];

    if (perm === "allaccess") {
      permissionsList.forEach((key) => {
        if (key !== "allaccess" && key !== "noaccess") {
          setValue(`permissions.${category}.${key}`, checked);
        }
      });
      setValue(`permissions.${category}.noaccess`, false);
      setValue(`permissions.${category}.allaccess`, checked);
    } else if (perm === "noaccess") {
      permissionsList.forEach((key) => {
        if (key !== "noaccess") {
          setValue(`permissions.${category}.${key}`, false);
        }
      });
      setValue(`permissions.${category}.noaccess`, checked);
    } else {
      setValue(`permissions.${category}.${perm}`, checked);
      if (checked) {
        setValue(`permissions.${category}.noaccess`, false);
      }
    }

    const currentPermissions = getValues(`permissions.${category}`);
    const allChecked = permissionsList
      .slice(0, 4)
      .every((key) => currentPermissions[key]);
    setValue(`permissions.${category}.allaccess`, allChecked);
  };

  const onSubmit = async (data) => {
    const formattedData = [
      {
        name: data.roleName,
        status: data.status === "Active" ? "0" : "1",
        permissions: data.permissions
      }
    ];
    await dispatch(updateRoleRequest(formattedData, id));
  };
  useEffect(() => {
    if (success) {
      toast.success("Role Updated Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetRoleState());
          navigate(ROLES); // Navigate after the toast is closed
        }
      });
    }
  }, [success]);
  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetRoleState());
        }
      });
    }
  }, [error]);
  // useEffect(() => {
  //   if (success) {
  //     toast.success("Role updated successfully!", {
  //       position: "top-center",
  //       autoClose: 3000,
  //     });
  //     navigate(ROLES);
  //   }
  //   if (error) {
  //     toast.error(error.message || "Failed to update role.");
  //   }
  // }, [success, error, navigate]);
  if (viewLoading) {
    return <Loading text="Loading Role Details..." />;
  }
  return (
    <>
      <ToastContainer />
      <Box className="role-permission-container">
        <Card>
          <CardContent className="card">
            <Typography
              variant="h5"
              gutterBottom
              className="form-heading"
              mb={3}
            >
              Edit Role
            </Typography>

            <Box className="role-permission-input-container">
              <Controller
                name="roleName"
                control={control}
                rules={{ required: "Role Name is required" }}
                render={({ field }) => (
                  <TextInputField
                    label="Role Name *"
                    fullWidth
                    placeholder="Enter Role Name"
                    error={!!errors.roleName}
                    helperText={errors.roleName?.message}
                    {...field}
                  />
                )}
              />
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box mb={3}>
                <PermissionsTable
                  control={control}
                  permissionsData={roleData.permissions || {}}
                  handlePermissionChange={handlePermissionChange}
                />
              </Box>
              <Box className="role-status">
                <StatusRadioGroup
                  name="status"
                  control={control}
                  defaultValue={status}
                  rules={{ required: "Status is required" }}
                />
              </Box>

              {/* <Box className="role-status">
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: "Status is required" }}
                  render={({ field }) => (
                    <StatusRadioGroup
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />

                {errors.status && (
                  <Typography color="error" variant="caption">
                    {errors.status.message}
                  </Typography>
                )}
              </Box> */}

              <Box mt={3} className="role-permission-button-container">
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => navigate(-1)}
                  className="cancel-button"
                >
                  Back
                </Button>
                <CustomButton
                  variant="contained"
                  className="success-button"
                  type="submit"
                  loading={editLoading}
                  text={"submit"}
                />
                {/* <Button
                  type='submit'
                  variant='contained'
                  className='success-button'
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update'}
                </Button> */}
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
